import React, { useCallback, useEffect, useMemo } from 'react';
import { Container, Stack, Typography, Grid, Button, Card, Box } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { FiEdit } from 'react-icons/fi';

// icons
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ApartmentIcon from '@mui/icons-material/Apartment';
import WcIcon from '@mui/icons-material/Wc';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SecurityIcon from '@mui/icons-material/Security';
import DomainIcon from '@mui/icons-material/Domain';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

// components
import BrudCrumbs from '../../components/BreadCrumbs';
import Page from '../../components/Page';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import { getUserViewList } from '../../store/Slice/User/UserSlice';
import useUserSelector from '../../store/Slice/User/UserSelector';
import { UserApi } from '../../utils/Apis';
import { normalizeValue } from '../../utils/CommonFunction';
import { NOT_AVAILABLE } from '../Global/Constants';

const getIconBoxStyle = (label) => {
  switch (label) {
    case 'User ID':
    case 'First Name':
    case 'Last Name':
    case 'Gender':
      return { bgcolor: 'rgba(2, 132, 199, 0.08)', color: '#0284c7' };
    case 'Mobile Phone':
    case 'Email Address':
      return { bgcolor: 'rgba(16, 185, 129, 0.08)', color: '#10b981' };
    case 'City':
    case 'State':
      return { bgcolor: 'rgba(99, 102, 241, 0.08)', color: '#6366f1' };
    case 'User Type':
    case 'Department Name':
    case 'Access Level Name':
      return { bgcolor: 'rgba(245, 158, 11, 0.08)', color: '#f59e0b' };
    case 'Reporting Manager':
    case 'Relationship Manager':
      return { bgcolor: 'rgba(14, 165, 233, 0.08)', color: '#0ea5e9' };
    default:
      return { bgcolor: 'rgba(2, 132, 199, 0.08)', color: '#0284c7' };
  }
};

function ViewUser() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { rowId } = useParams();
  const { LoginData } = useLoginSelector();
  const { UserViewListData } = useUserSelector();

  const getUserViewApi = useCallback(() => {
    if (!rowId) return;
    const endpoint = `${UserApi?.viewUser}/${rowId}?loggedInUserId=${LoginData?.userId}`;
    dispatch(getUserViewList(endpoint));
  }, [rowId, LoginData?.userId, dispatch]);

  useEffect(() => {
    getUserViewApi();
  }, [getUserViewApi]);

  const userFields = useMemo(
    () => [
      {
        label: 'User ID',
        value: normalizeValue(UserViewListData?.userId, NOT_AVAILABLE),
        icon: BadgeOutlinedIcon
      },
      {
        label: 'First Name',
        value: normalizeValue(UserViewListData?.firstName, NOT_AVAILABLE),
        icon: PersonOutlineIcon
      },
      {
        label: 'Last Name',
        value: normalizeValue(UserViewListData?.lastName, NOT_AVAILABLE),
        icon: PersonOutlineIcon
      },
      {
        label: 'Email Address',
        value: normalizeValue(UserViewListData?.email, NOT_AVAILABLE),
        icon: EmailOutlinedIcon
      },
      {
        label: 'Gender',
        value: normalizeValue(UserViewListData?.gender, NOT_AVAILABLE),
        icon: WcIcon
      },
      {
        label: 'Mobile Phone',
        value: normalizeValue(UserViewListData?.mobile, NOT_AVAILABLE),
        icon: PhoneAndroidIcon
      },
      {
        label: 'City',
        value: normalizeValue(UserViewListData?.cityName, NOT_AVAILABLE),
        icon: LocationCityIcon
      },
      {
        label: 'State',
        value: normalizeValue(UserViewListData?.stateName, NOT_AVAILABLE),
        icon: ApartmentIcon
      },
      {
        label: 'User Type',
        value: normalizeValue(UserViewListData?.userType, NOT_AVAILABLE),
        icon: BadgeOutlinedIcon
      },
      {
        label: 'Department Name',
        value: normalizeValue(UserViewListData?.departmentName, NOT_AVAILABLE),
        icon: DomainIcon
      },
      {
        label: 'Access Level Name',
        value: normalizeValue(UserViewListData?.accessLevelName, NOT_AVAILABLE),
        icon: SecurityIcon
      },
      {
        label: 'Reporting Manager',
        value: normalizeValue(UserViewListData?.reportingManagerName, NOT_AVAILABLE),
        icon: SupervisorAccountIcon
      },
      {
        label: 'Relationship Manager',
        value: normalizeValue(UserViewListData?.relationshipManagerName, NOT_AVAILABLE),
        icon: SupportAgentIcon
      }
    ],
    [UserViewListData]
  );

  const currentStatus = UserViewListData?.status || NOT_AVAILABLE;

  return (
    <Page title="Partner Management System || User Details">
      <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 8, pt: 2.5 }}>
        <Container maxWidth="xl">
          {/* Header Row */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 3.5 }}
          >
            {/* Left: Back Button + Title + Subtitle */}
            <Stack spacing={0.5}>
              <Button
                onClick={() => navigate('/users')}
                startIcon={<ArrowBackIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: '13px',
                  p: 0,
                  minWidth: 0,
                  mb: 0.5,
                  textTransform: 'none',
                  '&:hover': { color: '#0f172a', bgcolor: 'transparent' }
                }}
              >
                Back to User List
              </Button>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: '#0f172a',
                  letterSpacing: '-0.5px'
                }}
              >
                User Details
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px', mt: 0.25 }}>
                View complete user profile, role access, and account information.
              </Typography>
            </Stack>

            {/* Right: Breadcrumbs, Status & Action */}
            <Stack direction="row" spacing={2} alignItems="center">
              <Box>
                <BrudCrumbs />
              </Box>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                  Current Status:
                </Typography>
                <Box
                  sx={{
                    bgcolor: '#fef3c7',
                    color: '#b45309',
                    px: 1.75,
                    py: 0.4,
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '0.2px',
                    border: '1px solid #fde68a'
                  }}
                >
                  {currentStatus}
                </Box>
              </Stack>

              <Button
                variant="contained"
                startIcon={<FiEdit size={16} />}
                onClick={() => navigate(`/users/edit/${rowId}`)}
                sx={{
                  bgcolor: '#0284c7',
                  color: '#fff',
                  borderRadius: '8px',
                  px: 2.2,
                  py: 0.7,
                  fontSize: 13,
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: '0 2px 8px rgba(2, 132, 199, 0.25)',
                  '&:hover': { bgcolor: '#0369a1' }
                }}
              >
                Edit User
              </Button>
            </Stack>
          </Stack>

          {/* User Details Card */}
          <Card
            sx={{
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
              p: { xs: 2.5, md: 3 },
              bgcolor: '#ffffff'
            }}
          >
            {/* Card Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 3, pb: 2, borderBottom: '1px solid #f1f5f9' }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '8px',
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <PersonOutlineIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontSize: '16px', fontWeight: 800, color: '#0f172a' }}>
                  User Profile &amp; Account Info
                </Typography>
              </Stack>
            </Stack>

            {/* Field Grid */}
            <Grid container spacing={3}>
              {userFields.map((item, index) => {
                const iconStyle = getIconBoxStyle(item.label);
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Stack direction="row" spacing={1.75} alignItems="center">
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '12px',
                          bgcolor: iconStyle.bgcolor,
                          color: iconStyle.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}
                      >
                        <item.icon sx={{ fontSize: 22 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '11.5px', color: '#64748b', fontWeight: 500 }}>
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 700,
                            color: '#0f172a',
                            mt: 0.2,
                            wordBreak: 'break-word'
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>
                );
              })}
            </Grid>
          </Card>
        </Container>
      </Box>
    </Page>
  );
}

export default ViewUser;
